<template>
	<div>
		<div v-if="isLoading">
			<div class="loader" style="margin: auto"></div>
		</div>
		<template v-else>
		<p v-if="!store.rollDirection.edgeId">
			{{ $t("weld.rollDirectionMode.noDirectionDefined") }}
		</p>
		<p v-else>
			{{ $t("weld.rollDirectionMode.adjustHint") }}
		</p>
		<div style="display: flex; align-items: center; gap: 12px">
				<div>
					<input
						id="rollDirectionAngle"
						type="number"
						v-model="angleProxy"
						min="-180"
						max="180"
						:disabled="store.article.isReadOnly"
					/>
				</div>
				<div>
					<label for="rollDirectionAngle">{{ $t("weld.rollDirectionMode.angleRangeLabel") }}</label>
				</div>
			</div>
		</template>
	</div>
</template>

<script setup lang="ts">
import { EditorMode } from "@/model/Enums";
import { BlexonErpApi } from "@/store/BlexonErpApi";
import { useMainStore } from "@/store/mainStore";
import * as THREE from "three";
import { watch, onMounted, onUnmounted, computed, ref } from "vue";

const store = useMainStore();
let timeout: ReturnType<typeof setTimeout> | null = null;
const isLoading = ref(false);
// Ensures the anchor face is pulsed only once per entry into the roll direction mode
// (showOrHide is also called on zoom changes and reactive property writes, so without
// this guard the dot would flash every frame).
let hasPulsedCurrentAnchor = false;

let fragmentShader = `
    uniform vec3 uEdgeDir;
    uniform vec3 uFaceNormal;
    uniform float uAngle; // in RADIANS
    varying vec3 vWorldPos;

    void main() {
        float scale = 4.0;
        float lineWidth = 0.1;

        // 1. Tangente: Projektion der Kantenrichtung auf die Fläche
        vec3 tangent = normalize(uEdgeDir - dot(uEdgeDir, uFaceNormal) * uFaceNormal);

        // 2. Bitangente: orthogonal zu tangent in der Fläche
        vec3 bitangent = normalize(cross(uFaceNormal, tangent));

        // 3. Rotierte Linienrichtung in der Fläche
        vec3 lineDir = normalize(tangent * cos(uAngle) + bitangent * sin(uAngle));

        // 4. ⬅ Linien verlaufen in lineDir → Projektion muss auf Richtung SENKRECHT dazu erfolgen!
        vec3 projDir = normalize(cross(uFaceNormal, lineDir));

        // 5. Projektion der Weltposition
        float coord = dot(vWorldPos, projDir);

        float pattern = fract(coord / scale);
        float isLine = step(pattern, lineWidth) + step(1.0 - pattern, lineWidth);
        isLine = clamp(isLine, 0.0, 1.0);

        vec3 baseColor = vec3(0.2, 0.6, 0.8);
        vec3 lineColor = vec3(0.0);
        vec3 finalColor = mix(baseColor, lineColor, isLine);

        gl_FragColor = vec4(finalColor, 1.0);
    }
`;

watch(
	() => store.zoom,
	() => {
		showOrHide();
	}
);

const vertexShader = `
    varying vec3 vWorldPos;

    void main() {
        vec4 worldPosition = modelMatrix * vec4(position, 1.0);
        vWorldPos = worldPosition.xyz;
        gl_Position = projectionMatrix * viewMatrix * worldPosition;
    }
`;

const angleProxy = computed({
	get: () => store.rollDirection.angle,
	set: (value: any) => {
		const newAngleNumber = Number(value);
		if (
			!isNaN(newAngleNumber) &&
			newAngleNumber >= 0 &&
			newAngleNumber <= 180 &&
			newAngleNumber !== store.rollDirection.angle
		) {
			store.rollDirection.angle = newAngleNumber;
			// debounce the updating of the angle in the backend.
			// it only happens once per second.
			if (timeout) clearTimeout(timeout);
			timeout = setTimeout(async () => {
				store.cam.setRollDirectionAngle();
			}, 1000);
		}
	},
});

const showOrHide = async () => {
	if (!store.cam.sceneGroup) return;
	if (store.editorMode === EditorMode.RollDirectionMode) {
		// Only fetch the roll direction once per model. If the backend has no roll direction
		// yet, edgeId stays null - reloading on every reactive change would trigger an
		// infinite refetch loop. cam.ts flips rollDirectionLoaded back to false when a reload
		// is actually needed (model reload, user defined a new roll direction).
		if (!store.rollDirection.edgeId && !store.rollDirectionLoaded && !isLoading.value) {
			isLoading.value = true;
			try {
				const rollDirection = await BlexonErpApi.getRollDirection(store.article.id);
				store.rollDirection.edgeId = rollDirection.edgeExportId;
				store.rollDirection.angle = rollDirection.angle;
				store.rollDirection.direction = rollDirection.direction;
				store.rollDirection.position = rollDirection.position;
				store.rollDirection.length = rollDirection.length;
				store.rollDirection.normal = rollDirection.normal;
				store.rollDirection.adjacentFaceId = rollDirection.adjacentFaceId;
			} finally {
				store.rollDirectionLoaded = true;
				isLoading.value = false;
			}
		} else if (store.rollDirection.edgeId) {
			removeMaterialOfRollDirection();
		}
		const direction = store.rollDirection.direction?.clone();

		const anchorFace = store.cam.model.bodies[0].faces.find(
			(f) => f.originalId === store.rollDirection.adjacentFaceId
		);
		if (anchorFace) {
			const pos = anchorFace.mesh.geometry.attributes.position;

			// Nimm z. B. die ersten 3 Vertices
			const v0 = new THREE.Vector3().fromBufferAttribute(pos, 0);
			const v1 = new THREE.Vector3().fromBufferAttribute(pos, 1);
			const v2 = new THREE.Vector3().fromBufferAttribute(pos, 2);

			// Berechne die Flächennormale
			const edge1 = new THREE.Vector3().subVectors(v1, v0);
			const edge2 = new THREE.Vector3().subVectors(v2, v0);
			const faceNormal = new THREE.Vector3().crossVectors(edge1, edge2).normalize();
			fragmentShader = fragmentShader.replace(
				/(float scale = )(.+?)(;)/,
				`$1${15 / store.zoom}$3`
			);
			const shaderMaterial = new THREE.ShaderMaterial({
				vertexShader,
				fragmentShader,
				uniforms: {
					uEdgeDir: { value: direction?.normalize() },
					uFaceNormal: { value: faceNormal },
					uAngle: { value: THREE.MathUtils.degToRad(-store.rollDirection.angle) },
				},
			});
			anchorFace.mesh.material = shaderMaterial;

			// The anchor face can be tiny (e.g. on narrow sheet metal edges); a pulsating
			// red dot makes it easy to spot when the mode is opened or a new direction is set.
			if (!hasPulsedCurrentAnchor) {
				store.cam.pulseHighlighter.pulse([anchorFace]);
				hasPulsedCurrentAnchor = true;
			}
		}
	} else {
		removeMaterialOfRollDirection();
		hasPulsedCurrentAnchor = false;
	}
};

const removeMaterialOfRollDirection = () => {
	const material = new THREE.MeshBasicMaterial({ vertexColors: true });
	store.cam.model.bodies[0].faces
		.filter((f) => f.isAnchorFace)
		.forEach((f) => (f.mesh.material = material));
};

onMounted(() => {
	showOrHide();
});

onUnmounted(showOrHide);

watch(() => store.editorMode, showOrHide);
watch(store.rollDirection, showOrHide);
// When the anchor face changes (fresh load, or the user picked a new edge), allow
// another pulse so the newly highlighted face can be pointed out again.
watch(
	() => store.rollDirection.adjacentFaceId,
	() => {
		hasPulsedCurrentAnchor = false;
	}
);
</script>
